import { useGenerateAwsKeysMutation } from '@src/api/graphql-codegen';
import { createContext, useContext, useEffect, useState } from 'react';
import { useAuthToken } from './useAuthToken';
import { useRolesPermissions } from './useRolesPermission';
import { UserRoleEnum } from '@src/types';
import { logger } from '../utils/logger';

interface AWSKeys {
  AccessKeyId: string;
  SecretKey: string;
  SessionToken: string;
  Expiration: string;
}

interface AWSKeysContextType {
  awsKeys: AWSKeys | null;
};

const initialType: AWSKeysContextType = {
  awsKeys: null
};

const AWSKeysContext = createContext(initialType);

export const AWSKeysProvider = ({children}: { children: React.ReactNode }) => {
  const [awsKeys, setAwsKeys] = useState<AWSKeys | null>(null);
  const { token } = useAuthToken();
  
  // Check if user has the required roles to generate AWS keys
  const hasRequiredRole = useRolesPermissions([UserRoleEnum.SuperAdmin, UserRoleEnum.ContentAdmin]);

  const [generate] = useGenerateAwsKeysMutation({
    onError(e) {
      logger.debug(`[AWS Keys] Generation error:`, e);
    },
    onCompleted(data) {
      if (data?.generateAWSKeys) {
        logger.debug('[AWS Keys] Successfully generated keys');
        setAwsKeys(data.generateAWSKeys);
      }
    }
  });

  useEffect(() => {
    // Only attempt to generate keys if user has the required role
    if (!awsKeys && token && hasRequiredRole) {
      logger.debug('[AWS Keys] User has required role, generating keys');
      generate();
    } else if (!hasRequiredRole) {
      logger.debug('[AWS Keys] User does not have required role, skipping key generation');
    }
  }, [awsKeys, generate, token, hasRequiredRole]);

  return (
    <AWSKeysContext.Provider value={{awsKeys}}>
      {children}
    </AWSKeysContext.Provider>
  );
}

export const useAWSKeys = () => useContext(AWSKeysContext);
